import { get, cloneDeep } from 'lodash';
import axios from 'axios';
import { setWatchlistCategories, setSelectedFilter } from './../../reducers/Watchlist';
import countriesCode from '../../config/countriesCode';
import { setHideBeatLoader, setShowBeatLoader, setSnackBarObj } from '../../reducers/Alerts';
import { changeDateFormat, getSelectedRegionId } from '../../utils/helpers';
import { formatFillingsData } from './WatchlistHelpers';
import { getState, dispatch } from 'config/store';
import { getValueByJsonType } from './WatchlistTableHelpers';
import { extractItemsByUserPlan } from 'global/GlobalActions';
import { typesSelection } from 'config/filterTypes';

let gridCancelTokens = [];

const changeDocType = docType => {
  const doc = { '10-K': '10k', '10-Q': '10q' };

  return doc[docType] ? doc[docType] : '10k';
};

export const getWatchlistCategories = () => {
  return async dispatch => {
    try {
      const response = await axios.get('/api/get_watchlist_groups_tickers');
      const responseData = get(response, 'data.data', null);
      if (responseData?.length) {
        dispatch(setWatchlistCategories(responseData));
      }
    } catch (e) {
      console.log(e);
    }
  };
};

export const getSavedScreens = async () => {
  try {
    const response = await axios.get('/api/user_watchlist_searches');
    const screens = response.data?.data;
    if (screens?.length) {
      const allowedRegions = extractItemsByUserPlan(null, typesSelection);
      const allowedRegionKeys = allowedRegions.map(region => region.key);
      return screens.filter(screen => allowedRegionKeys.includes(screen.search_json.selectedType));
    }
  } catch (error) {
    console.log('error', error);
  }
};

export const deleteSavedScreen = async id => {
  try {
    const response = await axios.delete('/api/delete_watchlist_search', {
      params: {
        filterId: id,
      },
    });
    return response.data?.error === false;
  } catch (error) {
    console.log('error', error);
  }
};

export const updateSavedScreen = (id, label, selectedType, searchJson) => {
  return async dispatch => {
    try {
      const response = await axios.put(
        '/api/update_watchlist_search',
        {
          searchJson,
          tableType: selectedType,
          filterLabel: label,
        },
        {
          params: {
            filterId: id,
          },
        }
      );
      if (response.data?.error === false) {
        dispatch(setSelectedFilter({ id: id, filterLabel: label }));
        dispatch(setSnackBarObj({ message: 'Filter updated successfully.', severity: 'success' }));
      } else {
        dispatch(setSnackBarObj({ message: 'Sorry, Unable to update filter.', severity: 'error' }));
      }
    } catch (error) {
      console.log('error', error);
    }
  };
};

export const saveScreen = (label, selectedType, searchJson) => {
  return async dispatch => {
    try {
      const response = await axios.post('/api/save_watchlist_searches', {
        searchJson: searchJson,
        tableType: selectedType,
        filterLabel: label,
      });
      const currSavedScreenId = response?.data?.data?.id;
      if (currSavedScreenId) {
        dispatch(setSelectedFilter({ id: currSavedScreenId, filterLabel: label }));
        dispatch(setSnackBarObj({ message: 'Filter saved successfully.', severity: 'success' }));
      } else {
        dispatch(setSnackBarObj({ message: 'Sorry, Unable to save filter.', severity: 'error' }));
      }
    } catch (error) {
      console.log('error', error);
    }
  };
};

export const addNewWatchlistCategory = payload => {
  return async (dispatch, getState) => {
    const watchlistCategories = getState().Watchlist.watchlistCategories;
    try {
      const response = await axios.post('/api/save_watchlist_group', payload);
      const category = get(response, 'data.data', null);
      if (category?.id) {
        const addedCategory = {
          id: category.id,
          name: category.name,
          isCheckBoxChecked: true, // new added category is default selected
          watchlists: [],
        };
        const copy = cloneDeep(watchlistCategories);
        copy.push(addedCategory);
        dispatch(setWatchlistCategories(copy));
        return { isSuccess: true, addedCategory };
      }
      return { isSuccess: false, addedCategory: null };
    } catch (e) {
      console.log(e);
      return { isSuccess: false, addedCategory: null };
    }
  };
};

export const deleteWatchlistCategory = id => {
  return async (dispatch, getState) => {
    const watchlistCategories = getState().Watchlist.watchlistCategories;
    try {
      const response = await axios.delete('/api/delete_watchlist_group', { data: { id } });
      if (response.data?.error === false) {
        dispatch(setWatchlistCategories(watchlistCategories.filter(cat => cat.id !== id)));
      }
    } catch (e) {
      console.log(e);
    }
  };
};

export const removeTickerFromWatchlistCategory = (company_id, watchlist_group_id) => {
  return async (dispatch, getState) => {
    try {
      const response = await axios.delete('/api/delete_user_watchlist', {
        params: {
          company_id,
          watchlist_group_id,
        },
      });
      const responsePayload = get(response, 'data', null);
      if (responsePayload && !responsePayload.error) {
        if (watchlist_group_id) {
          const copy = cloneDeep(getState().Watchlist.watchlistCategories);
          const catIndex = copy.findIndex(a => a.id === watchlist_group_id);
          if (catIndex !== -1) {
            const deleteIndex = copy[catIndex].watchlists?.findIndex(a => a.company_id === company_id);
            if (deleteIndex !== -1) {
              copy[catIndex].watchlists.splice(deleteIndex, 1);
              dispatch(setWatchlistCategories(copy));
              dispatch(setSnackBarObj({ message: 'Ticker removed from Watchlist' }));
            }
          }
        }
      } else {
        dispatch(setSnackBarObj({ message: 'Unable to removed Ticker from Watchlist', severity: 'error' }));
      }
    } catch (error) {
      console.log('error', error);
    }
  };
};

export const updateWatchlistCategory = (id, name) => {
  return async (dispatch, getState) => {
    const watchlistCategories = getState().Watchlist.watchlistCategories;
    try {
      const response = await axios.put('/api/update_watchlist_group', { id, name });
      if (response.data?.error === false) {
        const updatedCatIndex = watchlistCategories.findIndex(cat => cat.id === id);
        if (updatedCatIndex !== -1) {
          const copy = cloneDeep(watchlistCategories);
          copy[updatedCatIndex].name = name;
          dispatch(setWatchlistCategories(copy));
          return true;
        }
      }
    } catch (e) {
      console.log(e);
    }
  };
};

export const getWatchlist = async (selectedUniverse, selectedFileType, selectedType, selectedCategoryID) => {
  const cancelToken = axios.CancelToken.source();
  try {
    const user = getState().User.user;
    cancelGridApiCalls();
    gridCancelTokens.push(cancelToken);
    const response = await axios.get('/api/get_companies_data', {
      params: {
        auth_token: user.authentication_token,
        subject: selectedUniverse,
        doc_type: changeDocType(selectedFileType),
        selected_type: 'domestic',
        watchlist_group_id: selectedCategoryID,
      },
      cancelToken: cancelToken.token,
    });
    const data = get(response, 'data.data.content', []);
    return selectedType === 'global' ? data.filter(item => item.co === 'CA') : data;
  } catch (error) {
    console.log(error);
  }
};

export const getWatchlistTable2Data = async (
  docTypeObj,
  selectedUniverse,
  selectedType,
  selectedFileType,
  filtersObject,
  selectedCategoryID,
  selected_exchange,
  page_iteration
) => {
  const cancelToken = axios.CancelToken.source();
  try {
    dispatch(setShowBeatLoader());
    cancelGridApiCalls();
    gridCancelTokens.push(cancelToken);
    const response = await axios.get('/api/get_company_filing_listing', {
      cancelToken: cancelToken.token,
      params: {
        index: selectedFileType === 'all' ? docTypeObj.index : 'fillings_*',
        order: 'desc',
        limit: 500,
        subject: selectedUniverse,
        document_type: get(docTypeObj, 'value', []).join(','),
        selected_type: selectedType,
        page_iteration,
        selected_region: getSelectedRegionId(selectedType),
        ...(docTypeObj.countryCode && { country_code: docTypeObj.countryCode }),
        ...(docTypeObj.sourceName && { source_name: docTypeObj.sourceName }),
        // ...(docTypeObj.sourceNameWithCountryCode && {
        //   source_name_with_country_code: docTypeObj.sourceNameWithCountryCode
        // }),
        ...(selectedUniverse === 'watchlist' && { watchlist_group_id: selectedCategoryID }),
        ...(selectedFileType === 'all' && { search_from_multiple_indexes: true }),
        ...(selectedUniverse === 'all' && selected_exchange !== 'all' && { selected_exchange }),
        ...filtersObject,
      },
    });
    if (response?.data?.data) {
      const data = response.data.data.map(d => {
        const { sentimentValue, wordCountValue } = getValueByJsonType(d.json_type, d.sentiment, d.word_count);
        return {
          ...d,
          companyName: get(d, 'company_name', null), // need in sidebar and table
          documentType: d.document_type?.toLowerCase() === 'fmp-transcript' ? 'Earnings Call' : d.document_type,
          sentiment: sentimentValue,
          document_date: changeDateFormat(d.document_date),
          wordCount: wordCountValue,
          countryCode: get(d, 'countrycode', ''),
          countryName: get(
            countriesCode.find(c => c.code === get(d, 'countrycode', '')),
            'name',
            ''
          ),
          sector: get(d, 'sector', null),
          industry: get(d, 'industry', null),
          flag: d.source_social_id,
          companyId: d.company_id,
          sourceName: d.source_name,
          recentId: d.id, // need in comparison,
          periodDate: changeDateFormat(d.period_date),
          mktcap: d.market_cap,
          currCode: d.iso_code,
          sedarType: d.sedar_type,
          ...formatFillingsData(d.cfs),
        };
      });
      return {
        data,
        totalRows: response.data.totalCount ?? 0,
      };
    }
  } catch (e) {
    if (!axios.isCancel(e)) {
      console.log('error', e);
    }
  } finally {
    dispatch(setHideBeatLoader());
  }
};

let getCompaniesBySymbolsToken = null;

export const cancelGetCompaniesBySymbols = () => {
  if (getCompaniesBySymbolsToken) {
    getCompaniesBySymbolsToken.cancel();
    getCompaniesBySymbolsToken = null;
  }
};

const checkIsSymbolTicker = symbol => {
  // if all characters of symbol is in uppercase and has no space then it is a ticker
  // if symbol doesn't match its upper case or contains space then it is a company name
  const hasSpace = /\s/g.test(symbol);

  return symbol === symbol.toUpperCase() && !hasSpace;
};

const checkIsSymbolFound = (symbol, fetchedCompany) => {
  // the symbol will be considered to be found if one of the following condition is true
  // 1: if symbol is ticker then symbol must match with the ticker of fetchedCompany without changing the case to lower or upper
  // 2: if symbol is company name then symbol must match with the part of company name of fetchedCompany
  // e.g. if symbol is apple and the company name of fetchedCompany is Apple Inc., then symbol will be considered to be found

  const isSymbolTicker = checkIsSymbolTicker(symbol);

  const isMatchedWithTicker = isSymbolTicker && fetchedCompany.ticker === symbol;

  const isMatchedWithCompanyName =
    !isSymbolTicker && fetchedCompany.companyName.toLowerCase().includes(symbol.toLowerCase());

  return isMatchedWithTicker || isMatchedWithCompanyName;
};

const createPayloadForGetCompanyByNames = ({ symbolsArr, searchIndex = null }) => {
  const selectedExchangeOption = getState().WatchlistDialog.selectedExchangeOption;

  const compNamesArr = [];
  const tickersArr = [];

  symbolsArr.forEach(symbol => {
    if (checkIsSymbolTicker(symbol)) {
      tickersArr.push(symbol);
    } else {
      compNamesArr.push(symbol);
    }
  });

  return {
    compNamesArr: compNamesArr.length ? compNamesArr : undefined,
    tickersArr: tickersArr.length ? tickersArr : undefined,
    index: searchIndex ?? selectedExchangeOption.index,
    limit: 4000,
  };
};

const getCompanyByNames = async payload => {
  try {
    cancelGetCompaniesBySymbols();

    const cancelToken = axios.CancelToken.source();
    getCompaniesBySymbolsToken = cancelToken;

    const response = await axios.post('/api/watchlist/get_companies_for_csv', payload, {
      cancelToken: cancelToken.token,
    });

    if (response.data.data?.length) {
      return response.data.data.map(company => ({
        ticker: company.ticker,
        companyId: company.company_id,
        companyName: company.company_name,
        isFound: true,
      }));
    }
  } catch (error) {
    if (!axios.isCancel(error)) {
      console.log(error);
    }
  }
  return [];
};

const getAlteredCompaniesByName = ({ symbolsArr, companiesByName, iteration }) => {
  // symbolsArr: contains symbols that were sent in the payload
  // companiesByName: contains companies that came in the response

  const foundCompaniesAccumulatedData = [];
  const foundCompaniesCount = {}; // holds the data of which symbol is found how many times
  const notFoundSymbolsData = [];

  symbolsArr.forEach((symbol, symbolIndex) => {
    let found = false;
    const foundCompanies = [];

    companiesByName.forEach((company, companyIndex) => {
      if (checkIsSymbolFound(symbol, company)) {
        found = true;
        foundCompanies.push({ ...company, symbol, rowIndex: `${iteration}-${symbolIndex}-${companyIndex}` });
      }
    });

    if (found) {
      foundCompaniesAccumulatedData.push(...foundCompanies);
    } else {
      notFoundSymbolsData.push({ symbol, isFound: false, rowIndex: `${iteration}-${symbolIndex}` });
    }

    foundCompaniesCount[symbol] = (foundCompaniesCount[symbol] ?? 0) + foundCompanies.length;
  });

  const foundCompaniesData = foundCompaniesAccumulatedData.map(company => ({
    ...company,
    isMultiple: foundCompaniesCount[company.symbol] > 1,
  }));

  return { foundCompaniesData, notFoundSymbolsData };
};

export const getAndSetSelectedSymbols = async symbolsArr => {
  if (symbolsArr.length > 999) {
    dispatch(
      setSnackBarObj({ message: 'Symbols list is too long. Please shorten it to 999 or fewer.', severity: 'error' })
    );
    return [];
  }

  let selectedSymbols = [];

  const payload = createPayloadForGetCompanyByNames({ symbolsArr });
  const companiesByName = await getCompanyByNames(payload);
  const { foundCompaniesData, notFoundSymbolsData } = getAlteredCompaniesByName({
    symbolsArr,
    companiesByName,
    iteration: 'i1',
  });

  const notFoundSymbols = notFoundSymbolsData.map(val => val.symbol);

  // if there is any symbol that isn't found from the current search index
  // then fetch those symbols from 'fillings_*' index
  if (notFoundSymbols.length) {
    const payloadForGlobal = createPayloadForGetCompanyByNames({
      symbolsArr: notFoundSymbols,
      searchIndex: 'fillings_*',
    });
    const companiesByNameForGlobal = await getCompanyByNames(payloadForGlobal);
    const finalResultArray = getAlteredCompaniesByName({
      symbolsArr: notFoundSymbols,
      companiesByName: companiesByNameForGlobal,
      iteration: 'i2',
    });

    const { foundCompaniesData: finalFoundCompanies, notFoundSymbolsData: finalNotFoundSymbols } = finalResultArray;

    selectedSymbols = [...foundCompaniesData, ...finalFoundCompanies, ...finalNotFoundSymbols];
  } else {
    selectedSymbols = [...foundCompaniesData, ...notFoundSymbolsData];
  }

  return selectedSymbols;
};

export const cancelGridApiCalls = () => {
  gridCancelTokens.forEach(token => {
    token.cancel();
  });
  gridCancelTokens = [];
};
